// API Client utility for making authenticated requests to the EasyAppIcon API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.easyappicon.com/v1'

export const createApiClient = (getAuthToken, onUnauthorized = null) => {
  const makeRequest = async (endpoint, options = {}) => {
    const token = getAuthToken()
    const url = `${API_BASE_URL}${endpoint}`
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        // Handle 401 Unauthorized by signing out and redirecting
        if (response.status === 401 && typeof onUnauthorized === 'function') {
          onUnauthorized()
          const error = new Error('Session expired')
          error.status = 401
          error.handled = true
          throw error
        }

        // Try to get error message from response body
        let errorMessage = 'An error occurred. Please try again.'
        
        try {
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json()
            errorMessage = errorData.message || errorData.error || errorMessage
          }
        } catch (parseError) {
          // If we can't parse the error response, use status-based messages
          switch (response.status) {
            case 400:
              errorMessage = 'Invalid request. Please check your input and try again.'
              break
            case 401:
              errorMessage = 'You are not authorized to perform this action. Please log in again.'
              break
            case 403:
              errorMessage = 'You do not have permission to perform this action.'
              break
            case 404:
              errorMessage = 'The requested resource was not found.'
              break
            case 429:
              errorMessage = 'Too many requests. Please wait a moment and try again.'
              break
            case 500:
              errorMessage = 'A server error occurred. Please try again later.'
              break
            default:
              errorMessage = 'An unexpected error occurred. Please try again.'
          }
        }
        
        const error = new Error(errorMessage)
        error.status = response.status
        error.statusText = response.statusText
        throw error
      }
      
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      
      return response
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  return {
    get: (endpoint, options) => makeRequest(endpoint, { ...options, method: 'GET' }),
    post: (endpoint, data, options) => 
      makeRequest(endpoint, { 
        ...options, 
        method: 'POST', 
        body: JSON.stringify(data) 
      }),
    put: (endpoint, data, options) => 
      makeRequest(endpoint, { 
        ...options, 
        method: 'PUT', 
        body: JSON.stringify(data) 
      }),
    delete: (endpoint, data, options) => 
      makeRequest(endpoint, { 
        ...options, 
        method: 'DELETE',
        ...(data && { body: JSON.stringify(data) })
      }),
    
    // Convenience methods for common operations
    generateIcons: (data) => makeRequest('/generate', { 
      method: 'POST', 
      body: JSON.stringify(data) 
    }),
    getUsage: () => makeRequest('/usage'),
    getFormats: () => makeRequest('/formats'),
  }
}

export default createApiClient
